/**
 * Types for the Knowledge Bases management feature (registering the team's own
 * Bedrock KB under a logical bundle key and listing registered tenant KBs).
 * Pure TS — no React.
 */

import type { RegisterKnowledgeBaseArgs } from '../../api';

/**
 * The editable "Register a Knowledge Base" form. Mirrors
 * {@link RegisterKnowledgeBaseArgs} but every field is a string (freely
 * editable) until submitted. `bucketPrefix` is optional on the wire and may be
 * empty.
 */
export interface RegisterKbForm {
  bundleKey: string;
  knowledgeBaseId: string;
  dataSourceId: string;
  bucket: string;
  bucketPrefix: string;
  modelArn: string;
}

/** A fresh, empty register form. */
export function emptyRegisterForm(): RegisterKbForm {
  return {
    bundleKey: '',
    knowledgeBaseId: '',
    dataSourceId: '',
    bucket: '',
    bucketPrefix: '',
    modelArn: '',
  };
}

/**
 * True when the required fields (all except bucketPrefix, which may be empty)
 * are filled — the minimum to submit RegisterKnowledgeBase.
 */
export function isRegisterFormComplete(form: RegisterKbForm): boolean {
  return (
    form.bundleKey.trim().length > 0 &&
    form.knowledgeBaseId.trim().length > 0 &&
    form.dataSourceId.trim().length > 0 &&
    form.bucket.trim().length > 0 &&
    form.modelArn.trim().length > 0
  );
}

/** Narrows a complete {@link RegisterKbForm} into wire register args. */
export function formToRegisterArgs(form: RegisterKbForm): RegisterKnowledgeBaseArgs {
  return {
    bundleKey: form.bundleKey.trim(),
    knowledgeBaseId: form.knowledgeBaseId.trim(),
    dataSourceId: form.dataSourceId.trim(),
    bucket: form.bucket.trim(),
    bucketPrefix: form.bucketPrefix.trim(),
    modelArn: form.modelArn.trim(),
  };
}
